"use client";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { usePathname, useSearchParams } from "next/navigation";
import {
  TOOL_LOAD_TRACE_TIMEOUT_MS,
  TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED,
  classifyLiveToolPath,
  expireToolLoadTrace,
  finishToolLoadTrace,
  recordToolRouteCommit,
  startToolLoadTrace,
  type FinishedToolLoadTrace,
  type ToolContentReceipt,
  type ToolLoadTrace,
} from "@/lib/performance/tool-load-timing";

interface NavigationLoadingContextType {
  isNavigating: boolean;
  navigatingTo: string | null;
  toolLoadTrace: ToolLoadTrace | null;
  startNavigation: (href: string) => void;
  endNavigation: () => void;
  completeToolLoad: (
    receipt: ToolContentReceipt,
    paintedAtMs: number,
  ) => FinishedToolLoadTrace | null;
}

const NavigationLoadingContext = createContext<NavigationLoadingContextType | null>(null);

export function useNavigationLoading() {
  const ctx = useContext(NavigationLoadingContext);
  if (!ctx) {
    return {
      isNavigating: false,
      navigatingTo: null,
      toolLoadTrace: null,
      startNavigation: () => {},
      endNavigation: () => {},
      completeToolLoad: () => null,
    };
  }
  return ctx;
}

export function NavigationLoadingProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname() || "/";
  const searchParams = useSearchParams();
  const searchSignature = searchParams?.toString() ?? "";

  const [isNavigating, setIsNavigating] = useState(false);
  const [navigatingTo, setNavigatingTo] = useState<string | null>(null);
  const [toolLoadTrace, setToolLoadTrace] = useState<ToolLoadTrace | null>(null);
  const toolLoadTraceRef = useRef<ToolLoadTrace | null>(null);
  const previousSearchSignatureRef = useRef(searchSignature);

  const startNavigation = useCallback((href: string) => {
    const target = classifyLiveToolPath(href);
    const current = classifyLiveToolPath(pathname);
    if (target && target.surface === current?.surface) {
      if (toolLoadTraceRef.current) {
        toolLoadTraceRef.current = null;
        setToolLoadTrace(null);
      }
      setIsNavigating(false);
      setNavigatingTo(null);
      return;
    }

    setIsNavigating(true);
    setNavigatingTo(href);
    const trace =
      TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED && target
        ? startToolLoadTrace({
            traceId: globalThis.crypto.randomUUID(),
            targetPath: href,
            startedAtMs: performance.now(),
          })
        : null;
    toolLoadTraceRef.current = trace;
    setToolLoadTrace(trace);
  }, [pathname]);

  const endNavigation = useCallback(() => {
    setIsNavigating(false);
    setNavigatingTo(null);
    toolLoadTraceRef.current = null;
    setToolLoadTrace(null);
  }, []);

  useLayoutEffect(() => {
    const current = toolLoadTraceRef.current;
    if (
      current?.state !== "active" ||
      classifyLiveToolPath(pathname)?.surface !== current.targetSurface
    ) return;
    const next = recordToolRouteCommit(current, pathname, performance.now());
    if (next !== current) {
      toolLoadTraceRef.current = next;
    }
  }, [pathname]);

  useEffect(() => {
    const current = toolLoadTraceRef.current;
    if (current?.state === "active") {
      const committedRoute = classifyLiveToolPath(pathname);
      if (committedRoute?.surface !== current.targetSurface) {
        endNavigation();
        return;
      }
      setToolLoadTrace((published) => published === current ? published : current);
      setIsNavigating(false);
      setNavigatingTo(null);
      return;
    }
    if (current?.state === "terminal") {
      const committedPath = current.committedPaths.at(-1) ?? current.initialTargetPath;
      const currentRoute = classifyLiveToolPath(pathname)?.pathname ?? pathname;
      if (currentRoute !== committedPath) endNavigation();
      return;
    }
    if (!current) endNavigation();
  }, [pathname, endNavigation]);

  useEffect(() => {
    const changed = previousSearchSignatureRef.current !== searchSignature;
    previousSearchSignatureRef.current = searchSignature;
    if (changed && toolLoadTraceRef.current?.state === "terminal") {
      endNavigation();
    }
  }, [endNavigation, searchSignature]);

  useEffect(() => {
    if (toolLoadTrace?.state !== "active") return;
    const remaining = Math.max(
      0,
      TOOL_LOAD_TRACE_TIMEOUT_MS - (performance.now() - toolLoadTrace.startedAtMs),
    );
    const timeout = window.setTimeout(() => {
      const current = toolLoadTraceRef.current;
      if (current?.state !== "active" || current.traceId !== toolLoadTrace.traceId) return;
      const expired = expireToolLoadTrace(current, performance.now());
      toolLoadTraceRef.current = expired;
      setToolLoadTrace(expired);
      setIsNavigating(false);
      setNavigatingTo(null);
    }, Math.ceil(remaining) + 1);
    return () => window.clearTimeout(timeout);
  }, [toolLoadTrace]);

  const completeToolLoad = useCallback(
    (receipt: ToolContentReceipt, paintedAtMs: number): FinishedToolLoadTrace | null => {
      if (!TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED) return null;
      const current = toolLoadTraceRef.current;
      if (current?.state !== "active" || current.targetSurface !== receipt.surface) return null;
      const finished = finishToolLoadTrace(current, {
        kind: receipt.terminalKind,
        contentSource: receipt.source,
        atMs: paintedAtMs,
      });
      toolLoadTraceRef.current = finished;
      setToolLoadTrace(finished);
      setIsNavigating(false);
      setNavigatingTo(null);
      return finished;
    },
    [],
  );

  const value = useMemo(
    () => ({
      isNavigating,
      navigatingTo,
      toolLoadTrace,
      startNavigation,
      endNavigation,
      completeToolLoad,
    }),
    [isNavigating, navigatingTo, toolLoadTrace, startNavigation, endNavigation, completeToolLoad]
  );

  return (
    <NavigationLoadingContext.Provider value={value}>
      {children}
    </NavigationLoadingContext.Provider>
  );
}
